import React from 'react';
import Konva from 'konva';
import Rect from 'components/canvas/components/nodes/components/base-node/components/rect';
import WithCursor from 'components/canvas/components/with-cursor';
import useCurrentSurfacePermissions from 'components/canvas/hooks/use-current-surface-permissions';
import useFindNodeInCache from 'components/canvas/hooks/use-find-node';
import useUpdateNodePosition from 'components/canvas/hooks/use-update-node-position';
import useCanvas from 'components/canvas/hooks/use-canvas';
import useNodeDragAnimation from 'components/canvas/components/nodes/components/base-node/hooks/use-node-drag-animation';
import useProtocolOperations from 'components/canvas/components/nodes/components/protocol-node/hooks/use-protocol-operations';
import useSurfaceNodeMovedOnSurface from 'components/canvas/components/nodes/components/base-node/hooks/use-surface-node-moved-on-surface';
import useIsCanvasDraggable from 'components/canvas/hooks/use-is-canvas-draggable';
import useCurrentSurface from 'routes/surface/hooks/use-current-surface';
import useSurfacePermissions from 'components/canvas/hooks/use-surface-permissions';
import { useSelectedNodeOperations } from 'state/canvas/selected-node';
import { usePrevious } from 'hooks';
import { getDoesLinkStartFromNode } from 'state/canvas/node-linking';
import { useDraggingNodeIdOperations } from 'components/canvas/hooks/use-dragging-node-id';
import { useSetHighestZIndexToNode } from 'components/canvas/hooks/use-set-heighest-z-index-to-node';
import { useCollaboratingCursorOperations } from 'routes/surface/components/collaborating-users-cursors';
import { Group } from 'react-konva';
import { isMobile } from 'react-device-detect';
import { isEmpty, isFunction } from 'lodash';
import { set, compose } from 'lodash/fp';
import { notEmpty } from 'utils/js';
import { Children } from 'utils/types/react';
import { KonvaEventObject } from 'konva/lib/Node';
import { AnyObject, Position } from 'types';
import {
  SubSurfaceNode,
  SurfaceNodeType,
  SurfaceNodeUnion,
} from 'graphql/types';

export interface NodeProps {
  children?: Children;
  onClick?: (event: KonvaEventObject<MouseEvent>) => void;
  showNodeModal: (node: SurfaceNodeUnion) => void;
  onDragMove?: (
    event: KonvaEventObject<MouseEvent>,
    onDragMoveHandler?: (node: SurfaceNodeUnion) => void,
  ) => void;
  onDragEnd?: (event: KonvaEventObject<MouseEvent>) => void;
  onDragStart?: (event: KonvaEventObject<MouseEvent>) => void;
  node: SurfaceNodeUnion;
  width: number;
  height: number;
  style?: AnyObject;
  withinProtocol?: boolean;
  offsetX?: number;
  offsetY?: number;
  scaleX?: number;
  scaleY?: number;
  withForwardRef?: any;
  cursor?: string;
  zIndex?: number;
  groupProps?: AnyObject;
}

interface NodeContext {
  x: number;
  y: number;
  width: number;
  height: number;
  toggleIsSelected: Function;
  isDraggable: boolean;
  showNodeModal: (node: SurfaceNodeUnion) => void;
}

function BaseNode({
  children,
  width,
  height,
  onClick,
  showNodeModal,
  node,
  cursor = 'pointer',
  ...props
}: NodeProps & { forceUpdated?: string }) {
  const surfaceId = node?.surface?.id as string;

  const currentSurface = useCurrentSurface();

  const surface = (node.type === SurfaceNodeType.SubSurface
    ? node
    : node?.surface) as SubSurfaceNode; // All nodes on surface have parent

  const {
    canReadSurfaceNode,
    isSharedSurfaceReference,
  } = useSurfacePermissions(surface);

  const {
    canRepositionSurfaceNode,
    canAddToPlaceholderNode,
    canRemoveFromPlaceholderNode,
  } = useCurrentSurfacePermissions();

  const previousPointsTo = usePrevious(node.pointsTo);

  const linksRemoved = notEmpty(previousPointsTo) && isEmpty(node.pointsTo);

  const surfaceNodeMovedOnSurface = useSurfaceNodeMovedOnSurface();

  const [isCanvasDraggable] = useIsCanvasDraggable();

  const { setSelectedNode, getSelectedNode } = useSelectedNodeOperations();

  const { setDraggingNodeId } = useDraggingNodeIdOperations();

  const {
    getRelativePointerPosition,
    getPointerPosition,
    setGrabbingCursor,
    setPointerCursor,
  } = useCanvas();

  const { updateCursorPosition } = useCollaboratingCursorOperations();

  const { animateOnDragEnd, animateOnDragMove } = useNodeDragAnimation();

  const { updateNodePositionOnServer } = useUpdateNodePosition(surfaceId);

  const { protocolContainingNodeOnDragEnd } = useProtocolOperations();

  const { findNodeById } = useFindNodeInCache(surfaceId);

  // In case user is VIEWER that means he cannot reposition node but if its shared surface reference then he should be able to move it

  const canReposition =
    canRepositionSurfaceNode(node) || isSharedSurfaceReference;

  const isDraggable = isCanvasDraggable && canReposition;

  const setHighestZIndexToNode = useSetHighestZIndexToNode();

  const { x, y } = node.position as Position;

  let surfaceNodeMoveOperationIndex = 0;

  function toggleIsSelected() {
    const selectedNode = getSelectedNode() ? null : node.id;
    return setSelectedNode(selectedNode);
  }

  async function onDragEnd(event) {
    event.cancelBubble = true;

    props.onDragEnd && props.onDragEnd(event);

    surfaceNodeMoveOperationIndex = 0;

    if (event.target instanceof Konva.Group) {
      setDraggingNodeId('');
      if (node.__typename != 'SurfaceBackgroundNode') {
        animateOnDragEnd();
      }

      setPointerCursor();

      const position = event.target.position();

      const nodeWithLatestPosition = compose(
        set('position.x', position.x),
        set('position.y', position.y),
      )(node);

      if (canAddToPlaceholderNode() && canRemoveFromPlaceholderNode()) {
        protocolContainingNodeOnDragEnd(node, event.target);
      }

      updateNodePositionOnServer(nodeWithLatestPosition, position);
    }
  }

  function onDragMove(event) {
    function onDragMoveHandler(node) {
      if (event.target instanceof Konva.Group) {
        setGrabbingCursor();

        const position = event.target.position();

        if (currentSurface.isShared) {
          const cursorPosition = getRelativePointerPosition();

          if (cursorPosition) {
            updateCursorPosition({
              surface: currentSurface?.id,
              cursorPosition,
            });
          }
        }

        surfaceNodeMovedOnSurface({
          id: node.id,
          surface: node.surface.id,
          position,
          index: surfaceNodeMoveOperationIndex++,
        });
      }
    }

    event.cancelBubble = true;

    //setDraggingNodeId(node.id);
    if (node.__typename != 'SurfaceBackgroundNode') {
      animateOnDragMove(event, getPointerPosition() as Position);
    }

    if (props.onDragMove) {
      props.onDragMove(event, onDragMoveHandler);
    }

    return onDragMoveHandler(node);
  }

  function onDragStart(event) {
    event.cancelBubble = true;

    surfaceNodeMoveOperationIndex = 0;

    props.onDragStart && props.onDragStart(event);

    // This causes glitch because way of setting z index messes up global state
    // The position is set to old one while node dragging is in progress and has not yet been stored in global state(happens on drage end)
    //setHighestZIndexToNode(node);

    if (props.onDragMove) {
      props.onDragMove(event);
    }
  }

  function onClickHandler(event) {
    if (canReadSurfaceNode()) {
      if (getDoesLinkStartFromNode(node)) {
        return;
      }

      if (onClick) {
        return onClick(event);
      }

      // Fetch the latest data for node from cache
      const freshNode = findNodeById(node.id);

      showNodeModal(freshNode);
    }
  }

  const dragHandlers = canReposition
    ? { onDragStart, onDragMove, onDragEnd }
    : {};

  const eventHandlers = {
    ...(isMobile
      ? {
          ...dragHandlers,
          onTap: onClickHandler,
        }
      : {
          ...dragHandlers,
          onClick: onClickHandler,
        }),
  };

  const groupProps = {
    id: node.id,
    type: node?.type,
    isSurfaceNode: true,
    offsetX: props.offsetX,
    offsetY: props.offsetY,
    draggable: isDraggable,
    scaleX: props.scaleX,
    scaleY: props.scaleY,
    perfectDrawEnabled: false,
    x,
    y,
    ...eventHandlers,
    zIndex: props.zIndex,
    ...props.groupProps,
  };

  console.log(' BSAS ', isFunction(children));

  return React.useMemo(() => {
    return (
      <WithCursor type={cursor}>
        <Group {...groupProps}>
          {isFunction(children)
            ? children({
                Rect: (
                  <Rect
                    node={node}
                    style={props.style}
                    width={width}
                    height={height}
                  />
                ),
                width,
                height,
                x,
                y,
                toggleIsSelected,
                isDraggable,
                showNodeModal,
              } as NodeContext)
            : children}
        </Group>
      </WithCursor>
    );
  }, [
    isDraggable,
    node.name,
    //@ts-ignore
    node?.text,
    //@ts-ignore
    node?.url,
    node.updatedAt,
    node?.fetchedAt,
    props.forceUpdated,
    linksRemoved,
    width,
    height,
  ]);
}

export default BaseNode;
